import { Alert, Badge, Button, Card, CardHeader, DataTable, dialogs, EmptyState, Field, FormGrid, Input, Modal, MoneyInput, NumberInput, SegmentedControl, Select, Skeleton, Switch, Textarea, toast } from "@cafe/ui";
import { LIMIT_FEATURES, localDate, MODULE_FEATURES, PLATFORM_TIMEZONE, QUOTA_FEATURES } from "@cafe/shared";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Headset, Plus } from "lucide-react";
import { useState } from "react";
import { Link, useParams } from "react-router";
import { api, day, dt, errorText, http, INVOICE_STATUS, money, STATUS, useCan } from "../lib/api";
import { auditLabel, describe, DEVICE_STATUS, DEVICE_TYPE, EVENT, featureLabel, OVERRIDE_MODE, PERIOD, PERIOD_ADV, sourceLabel } from "../lib/labels";

type Item = { id: string; status: string; billing: string; quantity: number; endsAt: string | null; startedAt: string; trialEnd: string | null; reason: string | null; addon: { code: string; name: string; pricingModel: string } };
type Card = {
  managers: ManagerRow[];
  company: { id: string; name: string; country: string; baseCurrency: string; createdAt: string; platformNotes: string | null; supportAccessGrantedUntil: string | null; siteSlug: string | null; customDomain: string | null; customDomainVerifiedAt: string | null };
  owner: { name: string; email: string | null; phone: string | null; lastLoginAt: string | null; status: string } | null;
  activityOrders7d: number; branches: { id: string; name: string; isActive: boolean }[];
  devices: { id: string; name: string; type: string; status: string; lastSeenAt: string | null; appVersion: string | null }[];
  subscription: { status: string; billingPeriod: string; currency: string; currentPeriodEnd: string | null; trialEnd: string | null; cancelAtPeriodEnd: boolean; discountPercent: number; managerId: string | null; scheduledChange: { plan?: string; period?: string } | null; planVersion: { version: number; plan: { code: string; name: string } }; items: Item[] } | null;
  events: { id: string; type: string; payload: Record<string, unknown>; actorType: string; createdAt: string }[];
  overrides: { id: string; featureCode: string; mode: string; value: number | null; reason: string; expiresAt: string | null; revokedAt: string | null; createdAt: string }[];
  entitlements: { status: string; plan: string; features: { code: string; enabled: boolean; source: string | null }[]; limits: { code: string; max: number | null; used: number | null }[] };
};
type ManagerRow = { id: string; managerId: string | null; managerName: string | null; validFrom: string; validTo: string | null; state: "scheduled" | "current" | "past"; assignedByName: string | null; reason: string | null };
type Addon = { id: string; code: string; name: string; pricingModel: string; availability: { planId: string; mode: string }[] };
type Plan = { id: string; code: string; name: string };
type Preview = { total: number; currency: string; withSubscription?: { lastDay: string }; lines: { description: string; amount: number }[]; scheduled: { plan?: string; period?: string }; warnings: { code: string; message: string; details?: unknown }[]; trial: boolean; effectiveAt: string };
type Invoice = { id: string; number: string; status: string; kind: string; total: number; currency: string; issuedAt: string; dueDate: string; paidAt: string | null; lines: { description: string; amount: number }[] };

type AccessRequest = { id: string; mode: "read_only" | "full"; status: "pending" | "approved" | "denied" | "active" | "ended" | "cancelled" | "expired"; expiresAt: string; decidedByName: string | null; notified?: number };

/**
 * «Войти как клиент» — только с согласия клиента (оба режима): запрос → владелец или управляющий разрешает в Coca
 * (уведомление в приложении, Telegram, на почту) → вход один раз в течение 10 минут, сеанс до 60 минут.
 */
function ImpersonateModal({ companyId, onClose }: { companyId: string; onClose: () => void }) {
  const [form, setForm] = useState({ mode: "read_only", reason: "" });
  const [req, setReq] = useState<AccessRequest | null>(null);
  const [busy, setBusy] = useState(false);
  const poll = useQuery({
    queryKey: ["impersonation", req?.id], enabled: !!req,
    queryFn: () => http.get<AccessRequest>(`/impersonations/${req!.id}`),
    refetchInterval: (q) => (q.state.data && q.state.data.status !== "pending" ? false : 3000),
  });
  const cur = poll.data ?? req;
  const send = async () => {
    setBusy(true);
    try {
      setReq(await http.post<AccessRequest>(`/companies/${companyId}/impersonate`, form));
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  const open = async () => {
    // Окно открываем сразу по нажатию (иначе браузер блокирует всплывающее), адрес — после ответа сервера
    const win = window.open("about:blank", "_blank");
    setBusy(true);
    try {
      const r = await http.post<{ login: unknown; mode: string; expiresAt: string }>(`/impersonations/${cur!.id}/start`, {});
      const erp = import.meta.env.VITE_ERP_URL ?? "http://localhost:5173";
      const url = `${erp}/impersonate#${encodeURIComponent(JSON.stringify({ login: r.login, mode: r.mode, expiresAt: r.expiresAt }))}`;
      if (win) { win.opener = null; win.location.href = url; } else window.open(url, "_blank", "noopener");
      onClose();
    } catch (e) {
      win?.close();
      toast.error(errorText(e));
      void poll.refetch();
    } finally {
      setBusy(false);
    }
  };
  const cancel = async () => {
    if (cur && (cur.status === "pending" || cur.status === "approved")) await http.post(`/impersonations/${cur.id}/end`, {}).catch(() => undefined);
    onClose();
  };
  const again = () => { setReq(null); };
  const until = cur ? new Date(cur.expiresAt).toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" }) : "";

  const footer = !cur
    ? <><Button loading={busy} disabled={form.reason.trim().length < 3} onClick={() => void send()}>Запросить доступ</Button><Button variant="outline" onClick={onClose}>Отмена</Button></>
    : cur.status === "approved"
      ? <><Button loading={busy} onClick={() => void open()}>Открыть кабинет клиента</Button><Button variant="outline" onClick={() => void cancel()}>Отказаться</Button></>
      : cur.status === "pending"
        ? <Button variant="outline" onClick={() => void cancel()}>Отозвать запрос</Button>
        : <><Button onClick={again}>Запросить снова</Button><Button variant="outline" onClick={onClose}>Закрыть</Button></>;

  return (
    <Modal open onOpenChange={(o) => !o && void cancel()} title="Войти как клиент"
      description="Вход возможен только с разрешения владельца или управляющего кафе. Сеанс — до 60 минут; клиент видит, что вы вошли, и это попадёт в его журнал."
      footer={footer}>
      {!cur ? (
        <div className="flex flex-col gap-4">
          <SegmentedControl fullWidth aria-label="Режим" value={form.mode} onValueChange={(v) => setForm({ ...form, mode: v })} options={[{ value: "read_only", label: "Только смотреть" }, { value: "full", label: "Можно менять" }]} />
          <Field label="Причина" required hint="Её увидит клиент в запросе. Например, номер обращения"><Textarea value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} rows={2} /></Field>
        </div>
      ) : cur.status === "pending" ? (
        <div className="flex flex-col gap-3">
          <Alert tone="info" title="Ждём разрешения клиента">Владелец и управляющие получили запрос в Coca{cur.notified === 0 ? "" : ", в Telegram и на почту (если подключены)"}. Ответить нужно до {until}. Окно можно не закрывать — как только клиент ответит, здесь появится кнопка входа.</Alert>
          {cur.notified === 0 && <Alert tone="warning">Уведомление никому не ушло — попросите клиента открыть Coca: запрос появится сразу при входе.</Alert>}
        </div>
      ) : cur.status === "approved" ? (
        <Alert tone="success" title="Клиент разрешил вход">{cur.decidedByName ? `Разрешил(а): ${cur.decidedByName}. ` : ""}Войти можно до {until}, один раз. Режим: {cur.mode === "full" ? "можно менять" : "только смотреть"}.</Alert>
      ) : (
        <Alert tone={cur.status === "denied" ? "danger" : "warning"}>
          {cur.status === "denied" ? `Клиент отказал во входе${cur.decidedByName ? ` (${cur.decidedByName})` : ""}.` : cur.status === "expired" ? "Клиент не ответил вовремя — запрос истёк." : "Запрос закрыт."}
        </Alert>
      )}
    </Modal>
  );
}

/** Ручное подтверждение своего адреса: DNS у регистратора не обновляется, а владение доменом поддержка проверила иначе. */
type PackRow = { code: string; name: string; status: "pending" | "paid" | null; source: string | null; paidAt: string | null; importCount: number };

/** Готовые меню клиента: что куплено, подарить набор (откроется сразу, открытый счёт отменится). */
function MenuPacksCard({ companyId }: { companyId: string }) {
  const can = useCan();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["company", companyId, "menu-packs"], queryFn: () => http.get<PackRow[]>(`/companies/${companyId}/menu-packs`) });
  const grant = async (p: PackRow) => {
    if (!(await dialogs.confirm({ title: `Подарить «${p.name}»?`, description: "Набор откроется клиенту сразу и бесплатно. Если по нему выставлен неоплаченный счёт — счёт отменится.", confirmText: "Подарить" }))) return;
    try {
      await http.post(`/companies/${companyId}/menu-packs/${p.code}/grant`, {});
      toast.success("Набор открыт клиенту");
      void qc.invalidateQueries({ queryKey: ["company", companyId] });
      void qc.invalidateQueries({ queryKey: ["menu-packs"] });
    } catch (e) {
      toast.error(errorText(e));
    }
  };
  return (
    <Card>
      <CardHeader title="Готовые меню" description="Наборы меню с фото и техкартами: покупаются один раз" />
      <div className="px-2 pb-3">
        <DataTable rows={q.data} loading={q.isPending} rowKey={(p) => p.code}
          columns={[
            { key: "n", header: "Набор", cell: (p) => p.name, mobile: "title" },
            { key: "s", header: "Статус", cell: (p) => (p.status === "paid" ? <Badge tone="success">{p.source === "admin" ? "Подарен" : "Куплен"}</Badge> : p.status === "pending" ? <Badge tone="warning">Ждёт оплаты</Badge> : <span className="text-muted">—</span>), mobile: "subtitle" },
            { key: "i", header: "Загрузок", align: "end", cell: (p) => (p.status === "paid" ? p.importCount : "—") },
          ]}
          actions={(p) => p.status !== "paid" && can("subscriptions.manage") && <Button size="sm" variant="ghost" onClick={() => void grant(p)}>Подарить</Button>} />
      </div>
    </Card>
  );
}

function VerifyDomainModal({ c, onClose }: { c: Card; onClose: () => void }) {
  const act = useAct(c.company.id);
  const [reason, setReason] = useState("");
  return (
    <Modal open onOpenChange={(o) => !o && onClose()} title={`Подтвердить ${c.company.customDomain ?? ""}`} size="sm"
      footer={<><Button disabled={!reason.trim()} onClick={() => void act(() => http.post(`/companies/${c.company.id}/custom-domain/verify`, { reason }), "Адрес подтверждён").then((ok) => ok && onClose())}>Подтвердить</Button><Button variant="outline" onClick={onClose}>Отмена</Button></>}>
      <div className="flex flex-col gap-3">
        <Alert tone="warning">Сайт сразу начнёт открываться по этому адресу. Подтверждайте, только если убедились, что домен принадлежит клиенту.</Alert>
        <Field label="Как проверили" required><Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} /></Field>
      </div>
    </Modal>
  );
}

function useAct(id: string) {
  const qc = useQueryClient();
  return async (fn: () => Promise<unknown>, ok: string) => {
    try {
      await fn();
      toast.success(ok);
      void qc.invalidateQueries({ queryKey: ["company", id] });
      void qc.invalidateQueries({ queryKey: ["company-invoices", id] });
      return true;
    } catch (e) {
      toast.error(errorText(e));
      return false;
    }
  };
}

/** Смена менеджера: с сегодняшнего дня, с 1-го числа следующего месяца или с даты. Прошлые оплаты остаются за прежним менеджером. */
function ManagerModal({ c, onClose }: { c: Card; onClose: () => void }) {
  const act = useAct(c.company.id);
  const managers = useQuery({ queryKey: ["staff-options", "managers"], queryFn: () => http.get<{ id: string; name: string }[]>("/staff/options", { managers: 1 }) });
  const today = localDate(PLATFORM_TIMEZONE);
  const nextMonth = (() => { const d = new Date(`${today.slice(0, 7)}-01T00:00:00Z`); d.setUTCMonth(d.getUTCMonth() + 1); return d.toISOString().slice(0, 10); })();
  const [managerId, setManagerId] = useState(c.subscription?.managerId ?? "none");
  const [when, setWhen] = useState<"now" | "next_month" | "date">("now");
  const [date, setDate] = useState(nextMonth);
  const [reason, setReason] = useState("");
  const effectiveFrom = when === "now" ? undefined : when === "next_month" ? nextMonth : date;
  const save = () => void act(() => http.put(`/companies/${c.company.id}/manager`, { managerId: managerId === "none" ? null : managerId, effectiveFrom, reason: reason.trim() || undefined }), effectiveFrom ? `Смена менеджера запланирована с ${day(effectiveFrom)}` : "Менеджер сменён").then((ok) => ok && onClose());
  return (
    <Modal open onOpenChange={(o) => !o && onClose()} title="Сменить менеджера"
      description="Оплаты, полученные до смены, остаются за прежним менеджером. Новому засчитываются оплаты с выбранного момента. Задним числом сменить нельзя."
      footer={<><Button onClick={save}>Сохранить</Button><Button variant="outline" onClick={onClose}>Отмена</Button></>}>
      <div className="flex flex-col gap-4">
        <Field label="Новый менеджер">
          <Select value={managerId} onValueChange={setManagerId} options={[{ value: "none", label: "Не закреплён" }, ...(managers.data ?? []).map((u) => ({ value: u.id, label: u.name }))]} />
        </Field>
        <Field label="С какого момента">
          <SegmentedControl aria-label="С какого момента" fullWidth value={when} onValueChange={setWhen} options={[{ value: "now", label: "Сейчас" }, { value: "next_month", label: `С ${day(nextMonth)}` }, { value: "date", label: "С даты" }]} />
        </Field>
        {when === "date" && <Field label="Дата начала"><Input type="date" value={date} min={today} onChange={(e) => setDate(e.target.value)} /></Field>}
        <Field label="Причина" hint="Попадёт в журнал действий"><Input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={300} /></Field>
      </div>
    </Modal>
  );
}

/** Смена тарифа/периода с предпросмотром proration (ТЗ 03 §6.2). */
function PlanModal({ c, onClose }: { c: Card; onClose: () => void }) {
  const plans = useQuery({ queryKey: ["plans"], queryFn: () => http.get<Plan[]>("/plans") });
  const s = c.subscription!;
  const [planCode, setPlan] = useState(s.planVersion.plan.code);
  const [period, setPeriod] = useState(s.billingPeriod);
  const body = { ...(planCode !== s.planVersion.plan.code && { planCode }), ...(period !== s.billingPeriod && { period }), addons: [] };
  const changed = Object.keys(body).length > 1;
  const pv = useQuery({ queryKey: ["preview", c.company.id, body], queryFn: () => http.post<Preview>(`/companies/${c.company.id}/subscription/preview-change`, body), enabled: changed });
  const act = useAct(c.company.id);
  return (
    <Modal open onOpenChange={(o) => !o && onClose()} title="Сменить тариф или срок оплаты"
      footer={<><Button disabled={!changed || !pv.data || pv.data.warnings.some((w) => w.code === "DOWNGRADE_BLOCKED")} onClick={() => void act(() => http.post(`/companies/${c.company.id}/subscription/change`, body), "Изменение применено").then((ok) => ok && onClose())}>{pv.data?.total ? `Выставить счёт ${money(pv.data.total, pv.data.currency)}` : "Применить"}</Button><Button variant="outline" onClick={onClose}>Отмена</Button></>}>
      <div className="flex flex-col gap-4 text-sm">
        <FormGrid cols={2}>
          <Field label="Тариф"><Select value={planCode} onValueChange={setPlan} options={(plans.data ?? []).map((p) => ({ value: p.code, label: p.name }))} /></Field>
          <Field label="Как часто платит"><Select value={period} onValueChange={setPeriod} options={Object.entries(PERIOD).map(([value, label]) => ({ value, label }))} /></Field>
        </FormGrid>
        {pv.isError && <Alert tone="danger">{errorText(pv.error)}</Alert>}
        {pv.data && (
          <>
            {pv.data.warnings.map((w) => <Alert key={w.code} tone={w.code === "DOWNGRADE_BLOCKED" ? "danger" : "warning"}>{w.message}{w.code === "ADDONS_DROPPED" && `: ${(w.details as { name: string }[]).map((x) => x.name).join(", ")}`}</Alert>)}
            {(pv.data.scheduled.plan || pv.data.scheduled.period) && <Alert tone="info">Изменение начнёт действовать после окончания оплаченного срока: {dt(pv.data.effectiveAt)}</Alert>}
            {pv.data.lines.map((l, i) => <div key={i} className="flex justify-between"><span>{l.description}</span><span className="tabular-nums">{money(l.amount, pv.data!.currency)}</span></div>)}
            <div className="flex justify-between border-t border-dashed border-border pt-2 font-semibold"><span>К оплате</span><span>{money(pv.data.total, pv.data.currency)}</span></div>
          </>
        )}
      </div>
    </Modal>
  );
}

/** Последний день срока в N месяцев с даты `from` (YYYY-MM-DD). */
const termEnd = (from: string, months: number) => {
  const d = new Date(`${from}T00:00:00Z`);
  d.setUTCMonth(d.getUTCMonth() + months);
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().slice(0, 10);
};
const nextDay = (date: string) => new Date(Date.parse(`${date}T00:00:00Z`) + 86_400_000).toISOString().slice(0, 10);
/** Последний оплаченный день по `endsAt` (полночь после него) */
const lastDayOf = (endsAt: string) => localDate(PLATFORM_TIMEZONE, new Date(Date.parse(endsAt) - 1));

/** Срок «с — по» с быстрыми кнопками 1/3/6/12 месяцев. */
function TermFields({ from, to, onChange, today }: { from: string; to: string; onChange: (v: { from: string; to: string }) => void; today: string }) {
  return (
    <>
      <FormGrid cols={2}>
        <Field label="С" error={from < today ? "Не раньше сегодня" : undefined}><Input type="date" value={from} min={today} onChange={(e) => e.target.value && onChange({ from: e.target.value, to })} /></Field>
        <Field label="По (включительно)" error={to < from ? "Раньше даты начала" : undefined}><Input type="date" value={to} min={from} onChange={(e) => e.target.value && onChange({ from, to: e.target.value })} /></Field>
      </FormGrid>
      <div className="flex flex-wrap gap-2">
        {[1, 3, 6, 12].map((m) => <Button key={m} size="sm" variant={to === termEnd(from, m) ? "secondary" : "outline"} onClick={() => onChange({ from, to: termEnd(from, m) })}>{m} мес.</Button>)}
      </div>
    </>
  );
}

/**
 * Включение add-on (US-PA-01): платно (счёт) или бесплатно с причиной и сроком (BR-SaaS-06).
 * Срок: вместе с подпиской (до конца оплаченного срока, дальше — её счетами) или свой «с — по» (разовый счёт за эти дни).
 */
function AddonModal({ c, addon, onClose, term: initial }: { c: Card; addon: Addon; onClose: () => void; term?: { from: string } }) {
  const can = useCan();
  const today = localDate(PLATFORM_TIMEZONE);
  const [mode, setMode] = useState<"subscription" | "custom">(initial ? "custom" : "subscription");
  const [term, setTerm] = useState(() => ({ from: initial?.from ?? today, to: termEnd(initial?.from ?? today, 1) }));
  const custom = mode === "custom";
  const termValid = !custom || (term.from >= today && term.to >= term.from);
  const [billing, setBilling] = useState<"charge" | "free">("charge");
  const [qty, setQty] = useState<number | null>(1);
  const [reason, setReason] = useState("");
  const [until, setUntil] = useState("");
  const [trial, setTrial] = useState(false);
  const act = useAct(c.company.id);
  const dates = custom && (billing === "charge" || !trial) ? term : null;
  const body = { billing, quantity: qty ?? 1, ...dates, ...(billing === "free" && { reason, ...(trial ? { trialDays: 14 } : !custom && until ? { until: new Date(until).toISOString() } : {}) }) };
  // Платно — та же сумма, что попадёт в счёт
  const change = { addons: [{ code: addon.code, action: "enable", quantity: qty ?? 1, ...dates }] };
  const pv = useQuery({ queryKey: ["preview", c.company.id, change], queryFn: () => http.post<Preview>(`/companies/${c.company.id}/subscription/preview-change`, change), enabled: billing === "charge" && termValid });
  const charge = billing === "charge" && !!pv.data?.total;
  return (
    <Modal open onOpenChange={(o) => !o && onClose()} title={`Подключить «${addon.name}»`}
      footer={<><Button disabled={!termValid || (billing === "free" ? !reason.trim() : !pv.data || pv.isFetching)} onClick={() => void act(() => http.post(`/companies/${c.company.id}/addons/${addon.code}/enable`, body), charge ? "Выставлен счёт" : "Дополнение включено").then((ok) => ok && onClose())}>{charge ? `Выставить счёт ${money(pv.data!.total, pv.data!.currency)}` : "Включить"}</Button><Button variant="outline" onClick={onClose}>Отмена</Button></>}>
      <div className="flex flex-col gap-4">
        <SegmentedControl fullWidth aria-label="Оплата" value={billing} onValueChange={setBilling} options={[{ value: "charge", label: "Платно — выставить счёт" }, { value: "free", label: "Бесплатно" }]} />
        {addon.pricingModel === "per_unit" && <Field label="Количество"><NumberInput stepper value={qty} onValueChange={setQty} min={1} max={1000} /></Field>}
        {!(billing === "free" && trial) && (
          <>
            <SegmentedControl fullWidth aria-label="Срок" value={mode} onValueChange={setMode} options={[{ value: "subscription", label: billing === "free" ? "Без срока / до даты" : "С подпиской" }, { value: "custom", label: "Свой срок" }]} />
            {custom ? <TermFields from={term.from} to={term.to} today={today} onChange={setTerm} />
              : billing === "charge" && pv.data?.withSubscription && <p className="text-sm text-muted">До конца оплаченного срока — по {day(pv.data.withSubscription.lastDay)}, дальше продлевается вместе с подпиской.</p>}
            {custom && billing === "charge" && <p className="text-sm text-muted">Разовый счёт за выбранные дни, само не продлевается.</p>}
          </>
        )}
        {billing === "charge" && pv.isError && <Alert tone="danger">{errorText(pv.error)}</Alert>}
        {billing === "charge" && pv.data && (
          <div className="flex flex-col gap-2 text-sm">
            {pv.data.lines.map((l, i) => <div key={i} className="flex justify-between gap-3"><span>{l.description}</span><span className="tabular-nums">{money(l.amount, pv.data!.currency)}</span></div>)}
            <div className="flex justify-between border-t border-dashed border-border pt-2 font-semibold"><span>К оплате</span><span className="tabular-nums">{money(pv.data.total, pv.data.currency)}</span></div>
            <p className="text-muted">{pv.data.total ? "Дополнение включится после оплаты счёта." : "Дополнение входит в тариф или бесплатно — включится сразу."}</p>
          </div>
        )}
        {billing === "free" && (
          <>
            <Switch checked={trial} onCheckedChange={setTrial} label="Пробный период 14 дней" description="Попробовать можно только один раз" />
            {!trial && !custom && <Field label="Действует до" hint={can("catalog.manage") ? "Пусто — навсегда" : "Обязательно"}><Input type="date" value={until} onChange={(e) => setUntil(e.target.value)} /></Field>}
            <Field label="Причина" required><Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} /></Field>
          </>
        )}
      </div>
    </Modal>
  );
}

function OverrideModal({ c, onClose }: { c: Card; onClose: () => void }) {
  const [f, setF] = useState({ featureCode: "core.kds", mode: "grant", value: null as number | null, reason: "", expiresAt: "" });
  const act = useAct(c.company.id);
  const isLimit = /^(limit|quota)\./.test(f.featureCode);
  return (
    <Modal open onOpenChange={(o) => !o && onClose()} title="Включить или отключить функцию вручную"
      footer={<><Button disabled={!f.reason.trim()} onClick={() => void act(() => http.post(`/companies/${c.company.id}/overrides`, { featureCode: f.featureCode, mode: f.mode, value: isLimit ? f.value : null, reason: f.reason, expiresAt: f.expiresAt ? new Date(f.expiresAt).toISOString() : null }), "Сохранено").then((ok) => ok && onClose())}>Сохранить</Button><Button variant="outline" onClick={onClose}>Отмена</Button></>}>
      <FormGrid cols={2}>
        <Field label="Функция или ограничение"><Select value={f.featureCode} onValueChange={(v) => setF({ ...f, featureCode: v, mode: /^(limit|quota)\./.test(v) ? "set" : "grant" })} options={[...MODULE_FEATURES, ...LIMIT_FEATURES, ...QUOTA_FEATURES].map((x) => ({ value: x, label: featureLabel(x) }))} /></Field>
        <Field label="Что сделать"><Select value={f.mode} onValueChange={(v) => setF({ ...f, mode: v })} options={(isLimit ? ["set", "delta"] : ["grant", "revoke"]).map((x) => ({ value: x, label: OVERRIDE_MODE[x]! }))} /></Field>
        {isLimit && <Field label={f.mode === "delta" ? "Сколько добавить" : "Сколько разрешить"}><NumberInput value={f.value} onValueChange={(v) => setF({ ...f, value: v })} /></Field>}
        <Field label="Действует до" hint="Пусто — навсегда"><Input type="date" value={f.expiresAt} onChange={(e) => setF({ ...f, expiresAt: e.target.value })} /></Field>
      </FormGrid>
      <Field label="Причина" required><Textarea value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} rows={2} /></Field>
    </Modal>
  );
}

type RenewalPreview = { currency: string; period: string | null; start: string; end: string; lines: { description: string; amount: number }[]; subtotal: number; discount: number; total: number };

/** Счёт на продление: за месяц/квартал/год (дальше счета — за этот период) или по выбранную дату, со скидкой. */
function RenewalModal({ c, onClose }: { c: Card; onClose: () => void }) {
  const s = c.subscription!;
  const act = useAct(c.company.id);
  const [period, setPeriod] = useState<string>(s.billingPeriod);
  const [until, setUntil] = useState(() => termEnd(localDate(PLATFORM_TIMEZONE), 1));
  const [percent, setPercent] = useState<number | null>(Number(s.discountPercent) || null);
  const [amount, setAmount] = useState<number | null>(null);
  const [comment, setComment] = useState("");
  const body = { period, ...(period === "custom" && { until }), discountPercent: percent ?? 0, ...(amount && { discountAmount: amount }), ...(comment.trim() && { comment: comment.trim() }) };
  const pv = useQuery({ queryKey: ["renewal-preview", c.company.id, body], queryFn: () => http.post<RenewalPreview>(`/companies/${c.company.id}/invoices/renewal/preview`, body) });
  const p = pv.data;
  return (
    <Modal open onOpenChange={(o) => !o && onClose()} title="Счёт на продление"
      footer={<><Button disabled={!p || pv.isFetching || pv.isError} onClick={() => void act(() => http.post(`/companies/${c.company.id}/invoices/renewal`, body), "Счёт выставлен").then((ok) => ok && onClose())}>{p ? `Выставить счёт ${money(p.total, p.currency)}` : "Выставить счёт"}</Button><Button variant="outline" onClick={onClose}>Отмена</Button></>}>
      <div className="flex flex-col gap-4 text-sm">
        <SegmentedControl fullWidth aria-label="За какой срок" value={period} onValueChange={setPeriod} options={[...Object.entries(PERIOD).map(([value, label]) => ({ value, label })), { value: "custom", label: "По дату" }]} />
        {period === "custom" && <Field label="Оплачено по (включительно)"><Input type="date" value={until} onChange={(e) => e.target.value && setUntil(e.target.value)} /></Field>}
        {period !== "custom" && period !== s.billingPeriod && <Alert tone="info">После оплаты счета будут выставляться {PERIOD_ADV[period] ?? period}.</Alert>}
        <FormGrid cols={2}>
          <Field label="Скидка, %" hint={Number(s.discountPercent) ? `Постоянная скидка клиента — ${Number(s.discountPercent)}%` : undefined}><NumberInput value={percent} onValueChange={setPercent} min={0} max={100} /></Field>
          <Field label="Скидка суммой"><MoneyInput value={amount} onValueChange={setAmount} /></Field>
        </FormGrid>
        <Field label="Комментарий в счёте"><Input value={comment} onChange={(e) => setComment(e.target.value)} maxLength={300} /></Field>
        {pv.isError && <Alert tone="danger">{errorText(pv.error)}</Alert>}
        {!p ? !pv.isError && <Skeleton className="h-24" /> : (
          <div className="flex flex-col gap-2">
            <p className="text-muted">Оплаченный срок: {day(p.start)} – {day(new Date(Date.parse(p.end) - 1).toISOString())}</p>
            {p.lines.map((l, i) => <div key={i} className="flex justify-between gap-3"><span>{l.description}</span><span className="tabular-nums">{money(l.amount, p.currency)}</span></div>)}
            {p.discount > 0 && <div className="flex justify-between text-muted"><span>Скидка</span><span className="tabular-nums">−{money(p.discount, p.currency)}</span></div>}
            <div className="flex justify-between border-t border-dashed border-border pt-2 font-semibold"><span>К оплате</span><span className="tabular-nums">{money(p.total, p.currency)}</span></div>
          </div>
        )}
      </div>
    </Modal>
  );
}

function InvoicesTab({ c }: { c: Card }) {
  const can = useCan();
  const id = c.company.id;
  const act = useAct(id);
  const list = useQuery({ queryKey: ["company-invoices", id], queryFn: () => api<{ items: Invoice[] }>("GET", "/invoices", undefined, { withMeta: true, query: { companyId: id } }) });
  const [manual, setManual] = useState(false);
  const [renewal, setRenewal] = useState(false);
  const [amount, setAmount] = useState<number | null>(null);
  const [desc, setDesc] = useState("");
  const markPaid = (inv: Invoice) =>
    dialogs.confirm({
      title: `Отметить оплату ${inv.number}`, description: `${money(inv.total, inv.currency)} — подписка включится или продлится, клиент получит доступ.`, confirmText: "Деньги получены",
      onConfirm: async () => void act(() => http.post(`/invoices/${inv.id}/mark-paid`, { method: "bank_transfer", reference: `${inv.number}-${Date.now()}` }), "Оплата подтверждена"),
    });
  return (
    <Card>
      <CardHeader title="Счета" actions={can("billing.manage") && (
        <div className="flex gap-2">
          {c.subscription && <Button size="sm" variant="secondary" onClick={() => setRenewal(true)}>Счёт на продление</Button>}
          <Button size="sm" variant="secondary" leftIcon={<Plus />} onClick={() => setManual(true)}>Свой счёт</Button>
        </div>
      )} />
      <div className="px-2 pb-3">
        <DataTable rows={list.data?.items} loading={list.isPending} rowKey={(i) => i.id} empty={<EmptyState title="Счетов нет" />}
          columns={[
            { key: "n", header: "Номер", cell: (i) => <span className="font-medium tabular-nums">{i.number}</span>, mobile: "title" },
            { key: "k", header: "Тип", cell: (i) => ({ renewal: "Продление", change: "Смена тарифа", manual: "Свой счёт", menu_pack: "Готовое меню" })[i.kind] ?? i.kind, mobile: "subtitle" },
            { key: "d", header: "Выставлен", cell: (i) => dt(i.issuedAt) },
            { key: "t", header: "Сумма", align: "end", cell: (i) => <span className="tabular-nums">{money(i.total, i.currency)}</span> },
            { key: "s", header: "Статус", cell: (i) => <Badge tone={INVOICE_STATUS[i.status]?.tone ?? "neutral"} dot>{INVOICE_STATUS[i.status]?.label ?? i.status}</Badge> },
          ]}
          actions={(i) => can("billing.manage") && i.status === "open" && (
            <div className="flex gap-2">
              <Button size="sm" onClick={() => void markPaid(i)}>Отметить оплату</Button>
              <Button size="sm" variant="ghost" onClick={() => void act(() => http.post(`/invoices/${i.id}/void`, { reason: "Аннулирован менеджером" }), "Счёт аннулирован")}>Аннулировать</Button>
            </div>
          )} />
      </div>
      {renewal && <RenewalModal c={c} onClose={() => setRenewal(false)} />}
      {manual && (
        <Modal open onOpenChange={(o) => !o && setManual(false)} title="Выставить счёт на любую сумму"
          footer={<><Button disabled={!amount || !desc.trim()} onClick={() => void act(() => http.post(`/companies/${id}/invoices`, { description: desc, amount }), "Счёт выставлен").then((ok) => ok && setManual(false))}>Выставить</Button><Button variant="outline" onClick={() => setManual(false)}>Отмена</Button></>}>
          <Field label="Описание" required><Input value={desc} onChange={(e) => setDesc(e.target.value)} /></Field>
          <Field label="Сумма" required><MoneyInput value={amount} onValueChange={setAmount} /></Field>
        </Modal>
      )}
    </Card>
  );
}

function AuditTab({ id }: { id: string }) {
  const list = useQuery({ queryKey: ["audit", id], queryFn: () => api<{ items: { id: string; action: string; userName: string | null; createdAt: string; newValue: unknown }[] }>("GET", "/audit", undefined, { withMeta: true, query: { companyId: id } }) });
  return (
    <Card>
      <CardHeader title="Что делали сотрудники платформы с этим клиентом" />
      <div className="px-2 pb-3">
        <DataTable rows={list.data?.items} loading={list.isPending} rowKey={(r) => r.id} empty={<EmptyState title="Записей нет" />}
          columns={[
            { key: "at", header: "Время", cell: (r) => dt(r.createdAt), mobile: "subtitle" },
            { key: "a", header: "Действие", cell: (r) => auditLabel(r.action), mobile: "title" },
            { key: "u", header: "Кто", cell: (r) => r.userName ?? "Система" },
            { key: "v", header: "Подробности", cell: (r) => <span className="line-clamp-2 max-w-md text-xs text-muted">{describe(r.newValue)}</span> },
          ]} />
      </div>
    </Card>
  );
}

/** Карточка клиента (ТЗ 03 §8.2): обзор, подписка, функции и лимиты, счета, аудит; сеанс поддержки. */
export default function CompanyPage() {
  const { id } = useParams();
  const can = useCan();
  const act = useAct(id!);
  const q = useQuery({ queryKey: ["company", id], queryFn: () => http.get<Card>(`/companies/${id}`) });
  const addons = useQuery({ queryKey: ["addons"], queryFn: () => http.get<Addon[]>("/addons") });
  const plans = useQuery({ queryKey: ["plans"], queryFn: () => http.get<Plan[]>("/plans") });
  const [tab, setTab] = useState("subscription");
  const [dialog, setDialog] = useState<"plan" | "override" | "impersonate" | "manager" | null>(null);
  const [addonOn, setAddonOn] = useState<{ addon: Addon; term?: { from: string } } | null>(null);
  const [verifyDomain, setVerifyDomain] = useState(false);
  const [notes, setNotes] = useState<string | null>(null);
  if (q.isPending) return <Skeleton className="h-96" />;
  if (!q.data) return <EmptyState title="Компания не найдена" />;
  const c = q.data;
  const s = c.subscription;
  const planId = plans.data?.find((p) => p.code === s?.planVersion.plan.code)?.id;
  const manage = can("subscriptions.manage");
  const addonName = (code: string) => addons.data?.find((x) => x.code === code)?.name;

  const ask = (title: string, action: string, ok: string, extra: object = {}) =>
    dialogs.confirm({ title, description: "Действие будет записано в журнал.", confirmText: "Подтвердить", onConfirm: async () => void act(() => http.post(`/companies/${c.company.id}/subscription/${action}`, { reason: title, ...extra }), ok) });
  const disableAddon = (it: Item) =>
    dialogs.confirm({
      title: `Отключить «${it.addon.name}»?`, description: "Доступ к дополнению закроется сразу. Данные клиента сохранятся.", confirmText: "Отключить сейчас",
      onConfirm: async () => void act(() => http.post(`/companies/${c.company.id}/addons/${it.addon.code}/disable`, { when: "now", reason: "Отключено менеджером" }), "Дополнение отключено"),
    });
  return (
    <>
      <Link to="/companies" className="inline-flex items-center gap-1 text-sm text-muted hover:text-fg"><ArrowLeft className="size-4" />Компании</Link>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">{c.company.name}</h1>
          <p className="text-sm text-muted">{s ? `Тариф «${s.planVersion.plan.name}» · платит ${PERIOD_ADV[s.billingPeriod] ?? s.billingPeriod}` : "Без подписки"} · клиент с {dt(c.company.createdAt)}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {s && <Badge tone={STATUS[s.status]?.tone ?? "neutral"} dot>{STATUS[s.status]?.label ?? s.status}</Badge>}
          {can("impersonate") && <Button variant="secondary" leftIcon={<Headset />} onClick={() => setDialog("impersonate")}>Войти как клиент</Button>}
        </div>
      </div>
      <SegmentedControl aria-label="Разделы" value={tab} onValueChange={setTab} options={[{ value: "subscription", label: "Подписка" }, { value: "overview", label: "Обзор" }, { value: "features", label: "Что доступно" }, { value: "invoices", label: "Счета" }, { value: "audit", label: "Журнал" }]} />

      {tab === "overview" && (
        <div className="grid gap-4 lg:grid-cols-2">
          <Card>
            <CardHeader title="Владелец и активность" />
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 px-5 pb-5 text-sm">
              <dt className="text-muted">Владелец</dt><dd>{c.owner?.name ?? "—"}</dd>
              <dt className="text-muted">Email</dt><dd>{c.owner?.email ?? "—"}</dd>
              <dt className="text-muted">Последний вход</dt><dd>{dt(c.owner?.lastLoginAt)}</dd>
              <dt className="text-muted">Менеджер</dt>
              <dd className="flex flex-col items-start gap-1">
                <span className="flex flex-wrap items-center gap-2">
                  {c.managers.find((m) => m.state === "current")?.managerName ?? "Не закреплён"}
                  {can("companies.assign_manager") && s && <Button size="sm" variant="outline" onClick={() => setDialog("manager")}>Сменить</Button>}
                </span>
                {c.managers.filter((m) => m.state === "scheduled").map((m) => <Badge key={m.id} tone="info">с {day(m.validFrom)} — {m.managerName ?? "без менеджера"}</Badge>)}
              </dd>
              <dt className="text-muted">Заказов за 7 дней</dt><dd className="tabular-nums">{c.activityOrders7d}</dd>
              <dt className="text-muted">Страна / валюта</dt><dd>{c.company.country} / {c.company.baseCurrency}</dd>
              <dt className="text-muted">Разрешил поддержке войти</dt><dd>{c.company.supportAccessGrantedUntil ? `до ${dt(c.company.supportAccessGrantedUntil)}` : "не выдан"}</dd>
              <dt className="text-muted">Свой адрес сайта</dt>
              <dd className="flex flex-wrap items-center gap-2">
                {c.company.customDomain ?? "—"}
                {c.company.customDomain && (c.company.customDomainVerifiedAt
                  ? <Badge tone="success" dot>подтверждён</Badge>
                  : <><Badge tone="warning" dot>ждёт DNS</Badge>{can("subscriptions.manage") && <Button size="sm" variant="outline" onClick={() => setVerifyDomain(true)}>Подтвердить вручную</Button>}</>)}
              </dd>
            </dl>
          </Card>
          {verifyDomain && <VerifyDomainModal c={c} onClose={() => setVerifyDomain(false)} />}
          {dialog === "manager" && <ManagerModal c={c} onClose={() => setDialog(null)} />}
          <Card>
            <CardHeader title="Заметки менеджера" />
            <div className="flex flex-col gap-3 px-5 pb-5">
              <Textarea value={notes ?? c.company.platformNotes ?? ""} onChange={(e) => setNotes(e.target.value)} rows={5} aria-label="Заметки" />
              {can("companies.update") && <Button size="sm" className="self-start" disabled={notes === null} onClick={() => void act(() => http.patch(`/companies/${c.company.id}`, { platformNotes: notes }), "Сохранено")}>Сохранить</Button>}
            </div>
          </Card>
          {c.managers.length > 1 && (
            <Card className="lg:col-span-2">
              <CardHeader title="Кто был менеджером" description="Оплаты засчитываются менеджеру, который был закреплён в день оплаты" />
              <div className="px-2 pb-3">
                <DataTable rows={c.managers} rowKey={(m) => m.id}
                  columns={[
                    { key: "m", header: "Менеджер", cell: (m) => m.managerName ?? "Не закреплён", mobile: "title" },
                    { key: "p", header: "Период", cell: (m) => `${day(m.validFrom)} — ${m.validTo ? day(m.validTo) : "сейчас"}`, mobile: "subtitle" },
                    { key: "s", header: "", cell: (m) => <Badge tone={m.state === "current" ? "success" : m.state === "scheduled" ? "info" : "neutral"}>{m.state === "current" ? "сейчас" : m.state === "scheduled" ? "запланирован" : "был"}</Badge> },
                    { key: "b", header: "Кто назначил", cell: (m) => [m.assignedByName, m.reason].filter(Boolean).join(" · ") || "—" },
                  ]} />
              </div>
            </Card>
          )}
          <Card className="lg:col-span-2">
            <CardHeader title="Филиалы и устройства" />
            <div className="px-2 pb-3">
              <DataTable rows={c.devices} rowKey={(d) => d.id} empty={<EmptyState title={`Филиалов: ${c.branches.length}, устройств нет`} />}
                columns={[
                  { key: "n", header: "Устройство", cell: (d) => d.name, mobile: "title" },
                  { key: "t", header: "Тип", cell: (d) => DEVICE_TYPE[d.type] ?? d.type, mobile: "subtitle" },
                  { key: "s", header: "Состояние", cell: (d) => DEVICE_STATUS[d.status] ?? d.status },
                  { key: "v", header: "Версия программы", cell: (d) => d.appVersion ?? "—" },
                  { key: "l", header: "Был на связи", cell: (d) => dt(d.lastSeenAt) },
                ]} />
            </div>
          </Card>
        </div>
      )}

      {tab === "subscription" && s && (
        <>
          <Card>
            <CardHeader title={`Тариф «${s.planVersion.plan.name}»`} description={s.trialEnd && s.status === "trialing" ? `Пробный период до ${dt(s.trialEnd)}` : `Оплачено до ${dt(s.currentPeriodEnd)}`}
              actions={manage && (
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" onClick={() => setDialog("plan")}>Сменить тариф</Button>
                  <Button size="sm" variant="secondary" onClick={() => void ask("Продлить на 14 дней", "extend", "Продлено", { days: 14 })}>+14 дней</Button>
                  {["suspended", "cancelled", "past_due", "read_only", "expired"].includes(s.status)
                    ? <Button size="sm" variant="secondary" onClick={() => void ask("Возобновить подписку", "resume", "Возобновлена")}>Возобновить</Button>
                    : <Button size="sm" variant="secondary" onClick={() => void ask("Приостановить подписку", "suspend", "Приостановлена")}>Приостановить</Button>}
                  {s.status !== "cancelled" && <Button size="sm" variant="ghost" onClick={() => void ask("Отменить подписку в конце периода", "cancel", "Отменена")}>Отменить</Button>}
                </div>
              )} />
            {s.scheduledChange && (s.scheduledChange.plan || s.scheduledChange.period) && <div className="px-5 pb-4"><Alert tone="info">После окончания оплаченного срока: {[s.scheduledChange.plan && `тариф «${plans.data?.find((p) => p.code === s.scheduledChange!.plan)?.name ?? s.scheduledChange.plan}»`, s.scheduledChange.period && `оплата ${PERIOD_ADV[s.scheduledChange.period] ?? s.scheduledChange.period}`].filter(Boolean).join(", ")}</Alert></div>}
          </Card>
          <Card>
            <CardHeader title="Дополнения" description="Бесплатное включается сразу, платное — после оплаты счёта. При отключении данные клиента сохраняются." />
            <div className="px-2 pb-3">
              <DataTable rows={(addons.data ?? []).filter((a) => (a.availability.find((x) => x.planId === planId)?.mode ?? "purchasable") !== "unavailable")} rowKey={(a) => a.code}
                columns={[
                  { key: "n", header: "Дополнение", cell: (a) => a.name, mobile: "title" },
                  { key: "m", header: "В тарифе", cell: (a) => ((a.availability.find((x) => x.planId === planId)?.mode ?? "purchasable") === "included" ? <Badge tone="success">Уже входит</Badge> : <Badge>За доплату</Badge>), mobile: "subtitle" },
                  {
                    key: "s", header: "Подключено", align: "end",
                    cell: (a) => {
                      const it = s.items.find((x) => x.addon.code === a.code);
                      return (
                        <span className="inline-flex items-center gap-2">
                          {it && <span className="text-xs text-muted">{it.billing === "free" ? "бесплатно" : "платно"}{it.quantity > 1 ? ` × ${it.quantity}` : ""}{Date.parse(it.startedAt) > Date.now() ? ` с ${day(localDate(PLATFORM_TIMEZONE, new Date(it.startedAt)))}` : ""}{it.endsAt ? ` по ${day(lastDayOf(it.endsAt))}` : it.trialEnd ? ` пробно до ${dt(it.trialEnd)}` : ""}</span>}
                          {it && manage && it.status === "active" && it.billing === "charge" && it.endsAt && <Button size="sm" variant="ghost" onClick={() => setAddonOn({ addon: a, term: { from: nextDay(lastDayOf(it.endsAt!)) } })}>Продлить</Button>}
                          <Switch aria-label={a.name} checked={!!it && it.status !== "scheduled_cancel"} disabled={!manage} onCheckedChange={(v) => (v ? setAddonOn({ addon: a }) : it && void disableAddon(it))} />
                        </span>
                      );
                    },
                  },
                ]} />
            </div>
          </Card>
          <MenuPacksCard companyId={c.company.id} />
          <Card>
            <CardHeader title="История подписки" />
            <ul className="flex flex-col divide-y divide-border px-5 pb-4 text-sm">
              {c.events.map((e) => <li key={e.id} className="flex justify-between gap-3 py-2"><span>{EVENT[e.type] ?? e.type} <span className="text-xs text-muted">{describe(e.payload)}</span></span><span className="shrink-0 text-xs text-muted">{dt(e.createdAt)}</span></li>)}
            </ul>
          </Card>
        </>
      )}

      {tab === "features" && (
        <div className="grid gap-4 lg:grid-cols-2">
          <Card>
            <CardHeader title="Функции" description="Откуда: по тарифу, из дополнения или включено вручную" actions={can("entitlements.override") && <Button size="sm" leftIcon={<Plus />} onClick={() => setDialog("override")}>Включить вручную</Button>} />
            <ul className="grid gap-1.5 px-5 pb-5 text-sm">
              {c.entitlements.features.map((f) => <li key={f.code} className="flex justify-between gap-3"><span className={f.enabled ? "" : "text-muted"}>{featureLabel(f.code)}</span><span className="text-xs">{f.enabled ? <Badge tone="success">{sourceLabel(f.source, addonName) ?? "включено"}</Badge> : f.source ? <Badge tone="danger">{sourceLabel(f.source, addonName)}</Badge> : "нет"}</span></li>)}
            </ul>
          </Card>
          <div className="flex flex-col gap-4">
            <Card>
              <CardHeader title="Ограничения" description="Сколько использует / сколько разрешено" />
              <ul className="grid gap-2 px-5 pb-5 text-sm">
                {c.entitlements.limits.map((l) => <li key={l.code} className="flex justify-between"><span>{featureLabel(l.code)}</span><span className="tabular-nums">{l.used ?? "—"} / {l.max ?? "без ограничений"}</span></li>)}
              </ul>
            </Card>
            <Card>
              <CardHeader title="Включено или отключено вручную" />
              <div className="px-2 pb-3">
                <DataTable rows={c.overrides} rowKey={(o) => o.id} empty={<EmptyState title="Нет" />}
                  columns={[
                    { key: "f", header: "Функция", cell: (o) => <span className={o.revokedAt ? "text-muted line-through" : ""}>{featureLabel(o.featureCode)} · {OVERRIDE_MODE[o.mode]?.toLowerCase() ?? o.mode}{o.value !== null ? ` ${o.value}` : ""}</span>, mobile: "title" },
                    { key: "r", header: "Причина", cell: (o) => o.reason, mobile: "subtitle" },
                    { key: "e", header: "До", cell: (o) => dt(o.expiresAt) },
                  ]}
                  actions={(o) => !o.revokedAt && can("entitlements.override") && <Button size="sm" variant="ghost" onClick={() => void act(() => http.del(`/companies/${c.company.id}/overrides/${o.id}`), "Отменено")}>Отменить</Button>} />
              </div>
            </Card>
          </div>
        </div>
      )}

      {tab === "invoices" && <InvoicesTab c={c} />}
      {tab === "audit" && <AuditTab id={c.company.id} />}

      {dialog === "plan" && s && <PlanModal c={c} onClose={() => setDialog(null)} />}
      {dialog === "override" && <OverrideModal c={c} onClose={() => setDialog(null)} />}
      {addonOn && <AddonModal c={c} addon={addonOn.addon} term={addonOn.term} onClose={() => setAddonOn(null)} />}
      {dialog === "impersonate" && <ImpersonateModal companyId={c.company.id} onClose={() => setDialog(null)} />}
    </>
  );
}
